import axios from 'axios';
import {
  ElNotification, ElMessageBox, ElMessage, ElLoading,
} from 'element-plus';
import { saveAs } from 'file-saver';
import { useI18n } from 'vue-i18n';
import { getToken } from '@/utils/auth';
import errorCode from '@/utils/errorCode';
import { tansParams, blobValidate } from '@/utils/ruoyi';
import cache from '@/plugins/cache';
import useUserStore from '@/store/modules/user';
import lang from '@/lang/ja';
import defaultSettings from '@/settings';

let downloadLoadingInstance;
// 是否显示重新登录
export const isRelogin = { show: false };

axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8';
// 创建axios实例
const service = axios.create({
  // axios中请求配置有baseURL选项，表示请求URL公共部分
  baseURL: import.meta.env.VITE_APP_BASE_API,
  // 超时
  timeout: 60 * 1000,
});

// request拦截器
service.interceptors.request.use((config) => {
  // 是否需要设置 token
  const isToken = (config.headers || {}).isToken === false;
  // 是否需要防止数据重复提交
  const isRepeatSubmit = (config.headers || {}).repeatSubmit === false;
  if (getToken() && !isToken) {
    config.headers.Authorization = `Bearer ${getToken()}`; // 让每个请求携带自定义token 请根据实际情况自行修改
  }
  // get请求映射params参数
  if (config.method === 'get' && config.params) {
    let url = `${config.url}?${tansParams(config.params)}`;
    url = url.slice(0, -1);
    config.params = {};
    config.url = url;
  }
  if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put')) {
    const requestObj = {
      url: config.url,
      data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
      time: new Date().getTime(),
    };
    const requestSize = Object.keys(JSON.stringify(requestObj)).length; // 请求数据大小
    const limitSize = 5 * 1024 * 1024; // 限制存放数据5M
    if (requestSize >= limitSize) {
      console.warn(`[${config.url}]: ` + 'データが許容される 5M 制限を超えているため、送信することができません。');
      return config;
    }
    const sessionObj = cache.session.getJSON('sessionObj');
    if (sessionObj === undefined || sessionObj === null || sessionObj === '') {
      cache.session.setJSON('sessionObj', requestObj);
    } else {
      const s_url = sessionObj.url; // 请求地址
      const s_data = sessionObj.data; // 请求数据
      const s_time = sessionObj.time; // 请求时间
      const interval = 1000; // 间隔时间(ms)，小于此时间视为重复提交
      if (s_data === requestObj.data && requestObj.time - s_time < interval && s_url === requestObj.url) {
        const message = 'データは処理中です。再送信しないでください';
        console.warn(`[${s_url}]: ${message}`);
        return Promise.reject(new Error(message));
      }
      cache.session.setJSON('sessionObj', requestObj);
    }
  }
  return config;
}, (error) => {
  console.log(error);
  Promise.reject(error);
});

// 响应拦截器
service.interceptors.response.use(
  (res) => {
  // 未设置状态码则默认成功状态
    const code = res.data.code || 200;
    // 获取错误信息
    const msg = errorCode[code] || res.data.msg || errorCode.default;
    // 二进制数据则直接返回
    if (res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer') {
      return res.data;
    }
    if (code === 401) {
      if (!isRelogin.show) {
        isRelogin.show = true;
        ElMessageBox.confirm('ログインセッションの有効期限が切れました。再度ログインしてください。', '確認', { confirmButtonText: '再ログイン', cancelButtonText: 'キャンセル', type: 'warning' }).then(() => {
          isRelogin.show = false;
          useUserStore().logOut().then(() => {
            location.href = defaultSettings.homePath;
          });
        }).catch(() => {
          isRelogin.show = false;
        });
      }
      return Promise.reject(lang.global.invalidSession);
    } if (code === 500) {
      ElMessage({ message: msg, type: 'error' });
      return Promise.reject(new Error(msg));
    } if (code === 601) {
      ElMessage({ message: msg, type: 'warning' });
      return Promise.reject(new Error(msg));
    } if (code !== 200) {
      ElNotification.error({ title: msg });
      return Promise.reject('error');
    }
    return Promise.resolve(res.data);
  },
  (error) => {
    if (axios.isCancel(error) || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
      return Promise.reject(error);
    }
    console.log(`err${error}`);
    let { message } = error;
    if (message == 'Network Error') {
      message = 'サーバーに接続できません';
    } else if (message.includes('timeout')) {
      message = '通信がタイムアウトしました';
    } else if (message.includes('Request failed with status code')) {
      message = `サーバーエラー（${message.substr(message.length - 3)}）が発生しました`;
    }
    ElMessage({ message, type: 'error', duration: 5 * 1000 });
    return Promise.reject(error);
  },
);

// 通用下载方法
export function download(url, params, filename, config) {
  downloadLoadingInstance = ElLoading.service({ text: lang.global.downloading, background: 'rgba(0, 0, 0, 0.85)', customClass: 'app-request-loading' });
  return service.post(url, params, {
    transformRequest: [(params) => tansParams(params)],
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    responseType: 'blob',
    ...config,
  }).then(async (data) => {
    const isBlob = blobValidate(data);
    if (isBlob) {
      const blob = new Blob([data]);
      saveAs(blob, filename);
    } else {
      const resText = await data.text();
      const rspObj = JSON.parse(resText);
      const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode.default;
      ElMessage.error(errMsg);
    }
    downloadLoadingInstance.close();
  }).catch((r) => {
    console.error(r);
    ElMessage.error('ファイルのダウンロード中にエラーが発生しました。管理者に連絡してください。');
    downloadLoadingInstance.close();
  });
}

export default service;
